/*
Event dispatched at point of products LOADED.
Should return empty result for non-Search page.

example:
```
{
  event: 'search_results',
  search: {
    keywordReturned: 'Bananas%2CFruit%2CLoose',
    numberOfResults: 48,
    searchTerm: 'Bananas',
    searchRulesets: 'Geoff|Woods',
    type: 'regular',
  },
}
```
*/
import { getUrlParam } from 'utils/general';

import { getMultiSearchValues } from 'redux/modules/multi-search/selectors';
import { getSearchRulesets, getSearchOrigin } from 'redux/modules/search/selectors';
import getSearchTerm from 'redux/modules/search/selectors/get-search-term';
import { getLocationSearchParams } from 'redux/modules/routing/selectors';
import { getTotalMatches } from 'redux/modules/search-and-browse/selectors/get-total-matches';
import { getSearchHeaders } from 'redux/modules/header/selectors';
import { getCriteriaSearchTags } from 'redux/modules/search-and-browse/selectors/criteria';
import { METADATA_HEADER_KEY, parseMetadataHeader } from 'api/utils/metadata-header';

const SEARCH_RESULTS = 'search';

const normaliseSearchTerm = term => term.replace(/,/g, ' ').toLowerCase();

export default function transform(state) {
  const searchParams = getLocationSearchParams(state);
  const searchTerm =
    getSearchTerm(state) ||
    getUrlParam('ct', searchParams) ||
    getUrlParam('searchTerm', searchParams);
  const searchOrigin = getSearchOrigin(state);

  if (!searchTerm || searchTerm.length < 1) return {};

  const normalisedSearchTerm = normaliseSearchTerm(searchTerm);
  const multipleSearchTerms = getMultiSearchValues(state);
  const numberOfResults = getTotalMatches(state);
  const sortByTag = getUrlParam('sortBy', searchParams);
  const searchHeaders = getSearchHeaders(state);
  const searchTags = getCriteriaSearchTags(state);
  const metadataHeader = searchHeaders?.[METADATA_HEADER_KEY] ?? '';
  const { 'search-request-id': searchRequestId } = parseMetadataHeader(metadataHeader);
  const { searchAndBrowse: { redirectUrl } = {} } = state;

  const termType = () => {
    if (redirectUrl) return 'redirect';
    if (searchOrigin === 'multi' && sortByTag) {
      return 'multi-refined';
    }
    switch (searchOrigin) {
      case 'typed':
      case 'search_recommendation':
      case 'search_history':
        return 'regular';
      case 'multi':
      case 'redirect':
        return searchOrigin;
      default:
        return 'url';
    }
  };

  const sortType = () => {
    switch (true) {
      case sortByTag === 'RELEVANCE':
        return 'Relevance';
      case sortByTag === 'A_2_Z':
        return 'Alphabetical';
      case sortByTag === 'Z_2_A':
        return 'Alphabetical';
      case sortByTag === 'MOST_POPULAR':
        return 'Popularity';
      case sortByTag === 'PRICE_LOW_TO_HIGH':
        return 'Price: Low to High';
      case sortByTag === 'PRICE_HIGH_TO_LOW':
        return 'Price: High to Low';
      case sortByTag === 'NEWEST':
        return 'Newest';
      case sortByTag === 'OLDEST':
        return 'Oldest';
      case sortByTag === 'TOP_RATED':
        return 'Top rated';
      default:
        return 'Relevance';
    }
  };

  return {
    event: SEARCH_RESULTS,
    search_type: termType(),
    search_term: redirectUrl
      ? normalisedSearchTerm
      : searchTags.map(({ value }) => value).join(' '),
    search_term_type: searchOrigin === 'multi' ? 'from_list' : searchOrigin,
    original_search_term: normalisedSearchTerm,
    multi_search_term_list: multipleSearchTerms?.length ? multipleSearchTerms : undefined,
    ...(!redirectUrl && { number_of_results: numberOfResults }),
    search_request_id: searchRequestId,
    redirect_page: redirectUrl,
    sort_type: sortType(),
  };
}
